import { Alert, Col, FormInstance, Row } from 'antd';
import { Currency } from '@common/components';
import React, { useEffect, useState } from 'react';
import { DisputePayCodeInput } from './dispute-pay-code-input';
import { DisputeHoursInput } from './dispute-hours-input';
import {
  DisputeBillingAmountInput,
  DisputeBillingRateInput,
  RedoActionButton,
} from '@pay-bill/components';
import { AutoAnimatedHeight } from 'works/components/auto-animated-height';
import { Colors } from 'works/legacy/lib/style-helper';
import clsx from 'clsx';

export const DisputeValuesForm = ({
  form,
  loadingPreview,
  payCodes,
  billingRate,
  billingAmountCents,
  canChangeBillingAmount,
  showEstimationSymbol,
  onResetBillingRate = () => {},
}: {
  form: FormInstance;
  loadingPreview?: boolean;
  payCodes: string[];
  billingRate: number;
  billingAmountCents: number;
  canChangeBillingAmount?: boolean;
  showEstimationSymbol: boolean;
  onResetBillingRate: () => void;
}) => {
  const [isCustomRateCents, setIsCustomRateCents] = useState<boolean>(false);

  useEffect(() => {
    if (isCustomRateCents) {
      return;
    }

    const formattedRate = parseFloat(((billingRate || 0) / 100).toFixed(2));

    form.setFieldValue('finalRateCents', formattedRate);
  }, [form, billingRate, isCustomRateCents]);

  return (
    <>
      <div className="table-form">
        <Row gutter={[16, 0]}>
          <Col span={6}>
            <DisputePayCodeInput
              payCodes={payCodes}
              onSelect={() => {
                if (isCustomRateCents) {
                  setIsCustomRateCents(false);
                }
              }}
            />
          </Col>
          <Col span={6}>
            <div style={{ display: 'flex' }}>
              <DisputeBillingRateInput
                disabled={!billingRate}
                onSetRateCents={() => setIsCustomRateCents(true)}
              />
              {isCustomRateCents && (
                <RedoActionButton
                  tooltipTitle="Click to reset Billing Rate to the default Pay Code rate"
                  onRedoAction={() => {
                    setIsCustomRateCents(false);
                    onResetBillingRate();
                  }}
                />
              )}
            </div>
          </Col>
          <Col span={6}>
            <DisputeHoursInput form={form} />
          </Col>
          <Col span={6} style={{ paddingTop: canChangeBillingAmount ? 0 : 5 }}>
            {canChangeBillingAmount ? (
              <DisputeBillingAmountInput />
            ) : (
              <span
                id="final-rate-preview"
                className={clsx('text-heavy', {
                  'final-rate-preview--loading': loadingPreview,
                })}
              >
                <Currency value={billingAmountCents / 100} />
                {showEstimationSymbol && '*'}
              </span>
            )}
          </Col>
        </Row>
        <AutoAnimatedHeight visible={isCustomRateCents}>
          <div style={{ marginBottom: 12 }}>
            <Alert
              showIcon
              message="You're setting a different Billing Rate for this Pay Code."
              type="info"
              style={{
                backgroundColor: Colors.skyHyperLight,
                border: 0,
              }}
            />
          </div>
        </AutoAnimatedHeight>
      </div>
      <style jsx>{`
        .final-rate-preview--loading {
          opacity: 0.3;
        }
      `}</style>
    </>
  );
};
